import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ContextSwitchTypeaheadEntry } from '@app/menu/components/context-switcher/context-switch-typeahead-entry';
import { RouterService } from '@app/routing/router.service';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { loggerFor } from '@app/utils/logger';
import { isBlank } from '@app/utils/string';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-context-switcher',
  templateUrl: './context-switcher.component.html',
  host: {
    class: 'context-switcher-container',
  },
  standalone: false,
})
export class ContextSwitcherComponent implements OnChanges, OnInit {
  @ContentChild(TemplateRef)
  public readonly triggerTemplate!: TemplateRef<any>;

  @Input()
  allowPreviousNext: boolean = true;

  @Input()
  disabled: boolean = false;

  @Input()
  dropdownItemTemplate: TemplateRef<any>;

  @Input()
  entries: ContextSwitchTypeaheadEntry[] = [];

  @Input()
  hideArrow: boolean = false;

  @Input()
  initialRoute: string;

  @Input()
  labelTemplate: TemplateRef<any>;

  @Input()
  manual: boolean = false;

  @Input()
  placeholder: string;

  @Input()
  switchParameter!: string;

  @Output()
  valueChanged: EventEmitter<ContextSwitchTypeaheadEntry> = new EventEmitter<ContextSwitchTypeaheadEntry>();

  @ViewChild('dropdown', { static: false })
  public dropdown: TippyDirective;

  @ViewChild('input', { static: false })
  public input: ElementRef<HTMLInputElement>;

  public filteredEntries: ContextSwitchTypeaheadEntry[] = [];

  /**
   * The index of the currently highlighted entry (based on filteredEntries)
   */
  public highlightedIndex: number = 0;

  /**
   * The index of the currently selected entry (based on filteredEntries)
   */
  public selectedIndex: number = -1;

  public selectedValue: ContextSwitchTypeaheadEntry | null = null;

  public term: string;

  public disabled$: Observable<boolean>;

  private manualSwitch: boolean = false;
  private logger = loggerFor('ContextSwitcherComponent');

  public get manualTrigger(): boolean {
    return this.manual !== false;
  }

  constructor(
    private loadingBarService: LoadingBarService,
    private routerService: RouterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'entries' in changes &&
      changes.entries.currentValue != null &&
      changes.entries.firstChange === false
    ) {
      this.updateEntries(changes.entries.currentValue);
    }
  }

  ngOnInit() {
    if (this.switchParameter == null) {
      console.error('switchParameter is not specified!');
    }

    if (this.routerService.currentRouteData.params[this.switchParameter] == null) {
      console.error(
        `The current route does not specify the given switchParameter: ${this.switchParameter}`,
        this.routerService.currentRouteData.params
      );
    }

    this.disabled$ = this.loadingBarService.value$.pipe(map((value) => value > 0));

    this.selectedValue = {
      key: undefined,
      label: String(this.routerService.currentRouteData.params[this.switchParameter]),
      value: this.routerService.currentRouteData.params[this.switchParameter],
    };

    this.routerService.routeDataChange$
      .pipe(
        filter(({ current, old }) => {
          let changed = old == null;

          if (old != null && current.params[this.switchParameter] != null) {
            changed = current.params[this.switchParameter] !== old.params[this.switchParameter];
          }

          return changed;
        }),
        untilDestroyed(this)
      )
      .subscribe(({ current }) => {
        if (this.manualSwitch === false) {
          if (this.entries?.length > 0) {
            const selectedIndex = this.entries.findIndex(
              (entry) => String(entry.value) === current.params[this.switchParameter]
            );

            const entry = this.entries[selectedIndex];

            if (entry != null) {
              if (this.selectedValue != null && this.selectedValue.value !== entry.value) {
                this.valueChanged.emit(entry);
              }

              this.selectedIndex = selectedIndex;
              this.selectedValue = entry;
            }
          }
        }

        this.manualSwitch = false;
      });

    if (this.entries != null) {
      this.updateEntries(this.entries);
    }
  }

  /**
   * Dropdown has been closed or opened
   * @param state true if opened, false if closed
   */
  public dropdownOpenChanged(state: boolean) {
    if (state === true) {
      // Focus the input on opening the dropdown
      setTimeout(() => {
        this.input.nativeElement.focus();
      }, 0);
    } else {
      // Reset the highlighted index and the term on closing the dropdown
      this.reset();
    }
  }

  public emit(entry: ContextSwitchTypeaheadEntry) {
    this.manualSwitch = true;
    this.selectedValue = entry;
    this.selectedIndex = this.filteredEntries.findIndex((_) => _.value === entry.value);

    this.dropdown.hide();
    this.reset();

    this.valueChanged.emit(entry);
  }

  public noop() {}

  /**
   * Handle arrow down and arrow up events (i.e. changing the highlighted index accordingly).
   * ENTER to accept selected, or first from filtered list, or do nothing if filtered list is empty.
   * ECS to leave the sitcher.
   * @param event
   */
  public onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();

      // // Make sure that the currently selected entry isn't the last one
      // if (this.filteredEntries.length - 1 > this.highlightedIndex) {
      //   const nextItem = this.filteredEntries[this.highlightedIndex + 1]
      //
      //   if (nextItem.value === this.value) {
      //     // Handle the edge case that the selected key is the last entry in the list
      //     if (this.filteredEntries.length - 2 > this.highlightedIndex) {
      //       // Jump over the selected entry (i.e. select the entry after that one)
      //       this.highlightedIndex += 2
      //     }
      //   } else {
      //     // Highlight the next entry
      //     ++this.highlightedIndex
      //   }
      // }

      this.highlightedIndex = this.findNextAvailableIndex(this.highlightedIndex, this.filteredEntries, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();

      // // Make sure that the currently selected entry isn't the first one
      // if (this.highlightedIndex > 0) {
      //   const nextItem = this.filteredEntries[this.highlightedIndex - 1]
      //
      //   if (nextItem.value === this.value) {
      //     // Handle the edge case that the selected key is the first entry in the list
      //     if (this.highlightedIndex > 1) {
      //       // Jump over the selected entry (i.e. select the entry before that one)
      //       this.highlightedIndex -= 2
      //     }
      //   } else {
      //     // Highlight the previous entry
      //     --this.highlightedIndex
      //   }
      // }

      this.highlightedIndex = this.findNextAvailableIndex(this.highlightedIndex, this.filteredEntries, -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();

      let index = this.highlightedIndex;

      if (index < 0) {
        index = 0;
      }

      if (this.filteredEntries[index] != null && this.filteredEntries[index].meta?.skip === undefined) {
        this.emit(this.filteredEntries[index]);

        // Reset the highlighted index and the term on closing the dropdown
        this.reset();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.dropdown.hide();
    }
  }

  // In case Vimium extension is in use then the Escape event is not catched by the <input> DOM element.
  // And for that case we use additional event subscription on document, meaning that on ESC+ESC it will work.
  @HostListener('document:keydown.escape', ['$event'])
  onEsc(event: KeyboardEvent): void {
    if (!this.dropdown?.isVisible) {
      return;
    }
    // tslint:disable-next-line:deprecation
    if (event.keyCode === 27 || event.key === 'Escape') {
      event.preventDefault();
      this.dropdown.hide();
    }
  }

  public onSearchInput(eventTarget: EventTarget) {
    const target = eventTarget as HTMLInputElement;

    this.search(target.value);
  }

  /**
   * The user changed the search term
   * @param value
   */
  public search(value: string) {
    this.term = value;

    this.updateEntries(this.entries, value);
  }

  /**
   * Toggle the dropdown
   */
  public toggle(): void {
    this.dropdown.toggle();
  }

  /**
   * Reset the highlighted index and the term on closing the dropdown
   */
  public reset() {
    this.highlightedIndex = 0;
    this.input.nativeElement.value = '';
    this.term = '';

    this.updateEntries(this.entries, '');
  }

  public isString(value: boolean | string | undefined | null): boolean {
    return typeof value === 'string';
  }

  public triggerPrevious() {
    this.moveBy(-1);
  }

  public triggerNext() {
    this.moveBy(1);
  }

  /**
   * Find the next typeahead entry that is not marked with "skip"
   *
   * @param currentIndex The current index to use
   * @param entries The list to search in
   * @param direction 1 or -1 to indicate the direction of the movement
   */
  private findNextAvailableIndex(
    currentIndex: number,
    entries: ContextSwitchTypeaheadEntry[],
    direction: number
  ) {
    let newIndex = currentIndex;
    let skipped: number = 0;

    if (entries.length > 0) {
      do {
        newIndex += direction;

        if (newIndex >= entries.length) {
          newIndex -= entries.length;
        } else if (newIndex < 0) {
          newIndex = entries.length + direction;
        }
      } while (
        newIndex !== this.selectedIndex &&
        entries[newIndex].meta?.skip != null &&
        skipped++ < entries.length
      );
    }

    return newIndex;
  }

  private moveBy(amount: number) {
    /**
     * We cannot use this.selectedIndex as it is based on this.filteredEntries and not on this.entries.
     * Therefore we need to find the index based on this.entries so the left/right navigation works correctly.
     */
    const selectedIndex = this.entries.findIndex((entry) => entry.value === this.selectedValue.value);

    let newIndex = this.findNextAvailableIndex(selectedIndex, this.entries, amount);

    this.emit(this.entries[newIndex]);
  }

  private sliceEntries(filteredUsers: ContextSwitchTypeaheadEntry[], selectedIndex: number) {
    const length = 10;
    const sliceWidth = length / 2;

    let returnValue = filteredUsers;

    if (filteredUsers.length > length) {
      let itemsBefore = sliceWidth;
      let itemsAfter = sliceWidth;

      if (selectedIndex < sliceWidth) {
        itemsBefore = selectedIndex;
        itemsAfter = length - itemsBefore;
      } else if (filteredUsers.length - selectedIndex <= sliceWidth) {
        itemsAfter = filteredUsers.length - (selectedIndex + 1);
        itemsBefore = length - itemsAfter;
      }

      let start = selectedIndex - itemsBefore;
      let end = selectedIndex + itemsAfter + 1;

      returnValue = filteredUsers.slice(start, end);
    }

    return returnValue;
  }

  private updateEntries(entries: ContextSwitchTypeaheadEntry[] = this.entries, term: string = this.term) {
    let filteredEntries;

    let selectedIndex = entries.findIndex(
      (entry) =>
        // Use == because we don't care about the type
        entry.value == this.selectedValue.value
    );

    if (selectedIndex === -1) {
      const routeValue = this.routerService.currentRouteData.params[this.switchParameter];
      selectedIndex = entries.findIndex((entry) => entry.value == routeValue);
    }

    if (selectedIndex > -1) {
      this.selectedValue = entries[selectedIndex];

      if (!isBlank(term)) {
        const lowerCaseTerm = term.toLowerCase();

        filteredEntries = entries.filter((user) => user.key.toLowerCase().indexOf(lowerCaseTerm) > -1);
      } else {
        filteredEntries = entries;
      }

      this.filteredEntries = this.sliceEntries(filteredEntries, selectedIndex);

      this.selectedIndex = this.filteredEntries.findIndex(
        // Use == because we don't care about the type
        (entry) => entry.value == this.selectedValue.value
      );

      this.highlightedIndex = this.selectedIndex;
    } else {
      this.filteredEntries = entries;
      this.logger.warn('No entry found for context, ignoring change', this.entries, this.selectedValue);
    }
  }
}
